import Lottie from 'lottie-react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type MouseEvent,
} from 'react';
import codAnimation from '@/assets/animations/COD.json';
import slaydTextIcon from '@/assets/svg/static/slaydText.svg';
import vid1 from '@/assets/video/vid1.mp4';
import vid2 from '@/assets/video/vid2.mp4';
import vid3 from '@/assets/video/vid3.mp4';
import { SkeletonLoader } from '@/components/ui';
import { useRazorpayWeb } from '@/hooks/useRazorpayWeb';
import { EVENTS, PROPERTIES, trackEvent } from '@/services/posthog';
import {
  fetchProductAdditionalInfo,
  fetchProductDataById,
} from '@/services/slaydAPI';
import useSlaylistSelectorStore from '@/stores/SlaylistSelectorStore';
import useLayoutStore from '@/stores/useLayoutStore';
import type {
  Product,
  ProductReview,
  ProductReviewData,
} from '@/types/Product.types';
import { decodeBase64Url, updatedImageUrl } from '@/utils/formatting';
import ProductDetailBottomCta from './ProductDetailBottomCta';
// Subcomponents
import ProductDetailCarousel from './ProductDetailCarousel';
import ProductDetailInfo from './ProductDetailInfo';
// Constants and Styles
import {
  DEFAULT_AI_REVIEW_SUMMARY,
  DEFAULT_CURATOR_NAME,
  DEFAULT_RATING,
  DEFAULT_RATING_COUNT,
  DEFAULT_REVIEWS_DISTRIBUTION,
  MOCK_REVIEWS_LIST,
  TOAST_DURATION,
} from './ProductDetailPageV2.constants';
import styles from './ProductDetailPageV2.module.css';
import ProductFooter from './ProductFooter';
import ProductReviewSection from './ProductReviewSection';
import ReelsPlayerOverlay from './ReelsPlayerOverlay';
import { REELS_LOCAL_VIDEOS_COUNT } from './ReelsPlayerOverlay/ReelsPlayerOverlay.constants';
import SizeSelectionSheet from './SizeSelectionSheet';
import ZoomableCarousel from './ZoomableCarousel';

const ProductDetailPage = (): JSX.Element => {
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Decode curator key if present in URL
  const key = searchParams.get('key');
  let curatorName = DEFAULT_CURATOR_NAME;
  if (key) {
    try {
      const decodedStr = decodeBase64Url(key);
      const parts = decodedStr.trim().split('|');
      if (parts[0]) curatorName = parts[0];
    } catch (err) {
      console.error('Error decoding curator key:', err);
    }
  }

  // Use id as component key to force re-render when product changes
  const componentKey = id || Date.now();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [productDetails, setProductDetails] = useState<Product>({} as Product);
  const [isProductLoading, setIsProductLoading] = useState<boolean>(true);

  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [sizeError, setSizeError] = useState<boolean>(false);

  // Bottom Sheets States
  const [showSizeSheet, setShowSizeSheet] = useState<boolean>(false);

  // Full-Screen Carousel States
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [activeFullScreenImages, setActiveFullScreenImages] = useState<
    string[]
  >([]);
  const [initialImageIndex, setInitialImageIndex] = useState<number>(0);
  const [activeReviewOverlays, setActiveReviewOverlays] = useState<
    ProductReview[] | undefined
  >(undefined);

  // Reviews State
  const [reviewsData, setReviewsData] = useState<ProductReviewData | null>(
    null
  );

  // Toast Notification state
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Reels Player State
  const [activeReelIndex, setActiveReelIndex] = useState<number | null>(null);

  const productSlaylistOverrides = useSlaylistSelectorStore(
    (state) => state.productSlaylistOverrides
  );

  // Ref to track overrides without causing re-renders
  const productSlaylistOverridesRef = useRef(productSlaylistOverrides);
  productSlaylistOverridesRef.current = productSlaylistOverrides;

  const { initiatePayment, status: paymentStatus } = useRazorpayWeb();
  const setNavbarVisibility = useLayoutStore(
    (state) => state.setNavbarVisibility
  );

  useEffect(() => {
    setNavbarVisibility(false);
    return () => {
      setNavbarVisibility(true);
    };
  }, [setNavbarVisibility]);

  const triggerToast = (message: string): void => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage(null);
    }, TOAST_DURATION);
  };

  const handleTryOnClick = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement | HTMLDivElement> | MouseEvent
    ): void => {
      e.preventDefault();
      e.stopPropagation();

      trackEvent(EVENTS.PDP_V2_TRY_ON_CLICK, {
        [PROPERTIES.PRODUCT_ID]: productDetails?.id,
        [PROPERTIES.PRODUCT_NAME]: productDetails?.name || '',
      });

      const element = document.getElementById('influencer-picks-section');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
    [productDetails?.id, productDetails?.name]
  );

  const handleShareClick = async (e: MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    const text = `Check out this product that I found on Slayd: ${productDetails.name}`;
    const url = window.location.href;

    trackEvent(EVENTS.PDP_V2_SHARE_CLICK, {
      [PROPERTIES.PRODUCT_ID]: productDetails.id,
      [PROPERTIES.PRODUCT_NAME]: productDetails.name,
    });

    if (navigator.share) {
      try {
        await navigator.share({
          title: productDetails.name || 'Slayd Originals',
          text,
          url,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        triggerToast('Link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy link:', err);
      }
    }
  };

  const handleReelClick = (index: number): void => {
    const video = influencerVideos[index];
    trackEvent(EVENTS.PDP_V2_REEL_CLICK, {
      [PROPERTIES.PRODUCT_ID]: productDetails?.id,
      [PROPERTIES.VIDEO_ID]: video?.id || index,
      [PROPERTIES.CURATOR_NAME]: curatorName,
    });
    setActiveReelIndex(index);
  };

  const fetchProductDetails = useCallback(
    async (options?: { isBackgroundUpdate?: boolean }) => {
      if (!id) return;

      try {
        if (!options?.isBackgroundUpdate) {
          setIsProductLoading(true);
        }

        const productData = await fetchProductDataById(id);
        if (productData) {
          if (!productData.isSlaydOriginals) {
            navigate(`/product/${id}`, { replace: true });
            return;
          }

          const transformedProduct: Product = {
            ...productData,
            isSlaydOriginals: productData.isSlaydOriginals || false,
          };

          setProductDetails(transformedProduct);
        }
      } catch (error) {
        console.error('Error fetching product details:', error);
      } finally {
        if (!options?.isBackgroundUpdate) {
          setIsProductLoading(false);
        }
      }
    },
    [id, navigate]
  );

  // Fetch reviews
  useEffect(() => {
    if (!id) return;
    fetchProductAdditionalInfo({ id, type: 'review' })
      .then((data) => {
        setReviewsData(data);
      })
      .catch((err) => {
        console.warn(
          'Reviews API failed or not found, falling back to mock high-fidelity review data:',
          err
        );
        setReviewsData({
          id: Number(id),
          overallRating: DEFAULT_RATING,
          totalRatings: DEFAULT_RATING_COUNT,
          totalReviews: DEFAULT_RATING_COUNT,
          ratingDistribution: DEFAULT_REVIEWS_DISTRIBUTION,
          reviews: MOCK_REVIEWS_LIST,
          aiReviewSummary: DEFAULT_AI_REVIEW_SUMMARY,
        });
      });
  }, [id]);

  useEffect(() => {
    if (id) {
      const locationStateProduct = location.state?.productData;
      if (locationStateProduct && locationStateProduct.id.toString() === id) {
        if (!locationStateProduct.isSlaydOriginals) {
          navigate(`/product/${id}`, { replace: true, state: location.state });
          return;
        }
        const override =
          productSlaylistOverridesRef.current[locationStateProduct.id];
        if (override !== undefined) {
          locationStateProduct.wishlists = override
            ? [{ product_list__name: override.name }]
            : [];
        }
        setProductDetails({
          ...locationStateProduct,
          availableSizes: locationStateProduct.availableSizes || null,
          isSlaydOriginals: locationStateProduct.isSlaydOriginals || false,
        });
        setIsProductLoading(false);
      } else {
        fetchProductDetails();
      }
    }
    // Reset state when ID changes
    setSelectedSize(null);
    setSizeError(false);
    setShowSizeSheet(false);
    setIsFullScreen(false);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [id, fetchProductDetails, navigate, location.state]);

  // Handle slaylist overrides
  useEffect(() => {
    if (productDetails?.id) {
      const override = productSlaylistOverrides[productDetails.id];
      if (override !== undefined) {
        setProductDetails((prev) => ({
          ...prev,
          wishlists: override
            ? [{ productList_Id: 0, productList_Name: override.name }]
            : [],
        }));
      }
    }
  }, [productSlaylistOverrides, productDetails?.id]);

  // Track Page View
  useEffect(() => {
    if (productDetails) {
      trackEvent(EVENTS.PDP_V2_VIEW, {
        [PROPERTIES.PRODUCT_ID]: productDetails.id,
        [PROPERTIES.PRODUCT_NAME]: productDetails.name,
        [PROPERTIES.PRICE]: productDetails.price,
        [PROPERTIES.BRAND]: productDetails.brand,
        [PROPERTIES.IS_AVAILABLE]: productDetails.isAvailable ?? false,
      });
    }
  }, [productDetails]);

  const startCheckout = useCallback((): void => {
    initiatePayment(productDetails, selectedSize || undefined).catch((err) => {
      console.error(
        '[PDP Buy Button] Checkout payment initiation failed:',
        err
      );
    });
  }, [initiatePayment, productDetails, selectedSize]);

  const handleBuyClick = useCallback(
    async (e: MouseEvent): Promise<void> => {
      e.preventDefault();
      e.stopPropagation();

      const availableSizes = productDetails.availableSizes;
      const hasSizes =
        availableSizes &&
        (typeof availableSizes === 'string'
          ? availableSizes.trim().length > 0
          : Array.isArray(availableSizes)
            ? (availableSizes as unknown[]).length > 0
            : false);

      trackEvent(EVENTS.PDP_V2_BUY_NOW_CLICK, {
        [PROPERTIES.PRODUCT_ID]: productDetails.id,
        [PROPERTIES.PRODUCT_NAME]: productDetails.name,
        [PROPERTIES.BRAND]: productDetails.brand,
        [PROPERTIES.HAS_SIZES]: !!hasSizes,
        [PROPERTIES.SELECTED_SIZE]: selectedSize || '',
      });

      if (hasSizes) {
        if (!selectedSize) {
          trackEvent(EVENTS.PDP_V2_SIZE_SHEET_OPEN, {
            [PROPERTIES.PRODUCT_ID]: productDetails.id,
            [PROPERTIES.AVAILABLE_SIZES]: availableSizes || '',
          });
          setShowSizeSheet(true);
        } else {
          startCheckout();
        }
        return;
      }

      startCheckout();
    },
    [productDetails, selectedSize, startCheckout]
  );

  const allImages = useMemo(() => {
    return [
      productDetails.imageUrl,
      ...(productDetails.secondaryImages || []),
    ].filter(Boolean);
  }, [productDetails.imageUrl, productDetails.secondaryImages]);

  const influencerVideos = useMemo(() => {
    if (
      productDetails.influencerVideos &&
      productDetails.influencerVideos.length > 0
    ) {
      return productDetails.influencerVideos;
    }

    const localVideos = [vid1, vid2, vid3];
    const items = [];
    for (let i = 0; i < REELS_LOCAL_VIDEOS_COUNT; i++) {
      const videoAsset = localVideos[i % 3];
      let thumb = productDetails.imageUrl;
      if (i % 3 === 1 && allImages[1]) thumb = allImages[1];
      if (i % 3 === 2 && allImages[2]) thumb = allImages[2];

      items.push({
        id: `local-reel-${i}`,
        videoUrl: videoAsset,
        thumbnailUrl: thumb,
        title: `Influencer Pick ${i + 1}`,
      });
    }
    return items;
  }, [productDetails.influencerVideos, productDetails.imageUrl, allImages]);

  const ratingVal =
    reviewsData?.overallRating ?? productDetails.rating ?? DEFAULT_RATING;
  const ratingCountVal =
    reviewsData?.totalRatings ??
    productDetails.ratingCount ??
    DEFAULT_RATING_COUNT;

  if (isProductLoading) {
    return <SkeletonLoader.pdp />;
  }

  return (
    <div className={styles.parentProductDetails}>
      <div className={styles.container} key={componentKey} ref={scrollRef}>
        {/* Top Header Branding */}
        <div className={styles.originalsHeader}>
          <h1 className={styles.originalsTitle}>
            <img
              src={slaydTextIcon}
              alt="Slayd"
              className={styles.slaydTextLogo}
            />
            <span className={styles.xText}>x</span>
            <span className={styles.originalsTitleCursive}>{curatorName}</span>
          </h1>
          <span className={styles.originalsSubtitle}>
            Trusted by 1lakh+ customers
          </span>
        </div>

        <div className={styles.gradientWrapper}>
          {/* Carousel Media component */}
          <ProductDetailCarousel
            productDetails={productDetails}
            allImages={allImages}
            ratingVal={Number(ratingVal)}
            ratingCountVal={ratingCountVal}
            onTryOnClick={handleTryOnClick}
            onImageClick={(index) => {
              setInitialImageIndex(index);
              setActiveFullScreenImages(allImages.map(updatedImageUrl));
              setActiveReviewOverlays(undefined);
              setIsFullScreen(true);
            }}
          />

          {/* Cash On Delivery Banner */}
          <div className={styles.codBanner}>
            <Lottie
              autoplay
              animationData={codAnimation}
              className={styles.codLottie}
              loop={true}
            />
          </div>
        </div>

        {/* Info detail component */}
        <ProductDetailInfo
          productDetails={productDetails}
          curatorName={curatorName}
          selectedSize={selectedSize}
          sizeError={sizeError}
          description={productDetails.description || ''}
          influencerVideos={influencerVideos}
          onSizeSelect={(size) => {
            setSelectedSize(size);
            setSizeError(false);
          }}
          onViewSizeChart={() => {
            if (productDetails.sizeChartUrl) {
              setInitialImageIndex(0);
              setActiveFullScreenImages([
                updatedImageUrl(productDetails.sizeChartUrl),
              ]);
              setActiveReviewOverlays(undefined);
              setIsFullScreen(true);
            } else {
              triggerToast('No size chart available for this product');
            }
          }}
          onReelClick={handleReelClick}
        />

        <div className={styles.dividerLine} />

        {/* Customer reviews component */}
        <ProductReviewSection
          reviewsData={reviewsData}
          ratingVal={Number(ratingVal)}
          ratingCountVal={ratingCountVal}
          productDetails={productDetails}
          allImages={allImages}
          onReviewImageClick={(images, index, overlays) => {
            setInitialImageIndex(index);
            setActiveFullScreenImages(images);
            setActiveReviewOverlays(overlays);
            setIsFullScreen(true);
          }}
        />

        {/* Creator / Policy Footer */}
        <ProductFooter
          curatorName={curatorName}
          productDetails={productDetails}
        />

        {/* Sticky Bottom CTA */}
        <ProductDetailBottomCta
          isAvailable={productDetails.isAvailable ?? false}
          paymentStatus={paymentStatus}
          onShareClick={handleShareClick}
          onBuyClick={handleBuyClick}
        />

        {/* Toast Notification */}
        {toastMessage && <div className={styles.toastWrap}>{toastMessage}</div>}

        {/* Size Selection Bottom Sheet Modal */}
        {showSizeSheet && (
          <SizeSelectionSheet
            availableSizes={productDetails.availableSizes}
            selectedSize={selectedSize}
            paymentStatus={paymentStatus}
            onSizeSelect={(size) => {
              trackEvent(EVENTS.PDP_V2_SIZE_SELECTED, {
                [PROPERTIES.PRODUCT_ID]: productDetails.id,
                [PROPERTIES.SIZE]: size,
              });
              setSelectedSize(size);
              setSizeError(false);
            }}
            onClose={() => {
              trackEvent(EVENTS.PDP_V2_SIZE_SHEET_CLOSE, {
                [PROPERTIES.PRODUCT_ID]: productDetails.id,
              });
              setShowSizeSheet(false);
            }}
            onContinue={() => {
              trackEvent(EVENTS.PDP_V2_SIZE_SHEET_CONTINUE, {
                [PROPERTIES.PRODUCT_ID]: productDetails.id,
                [PROPERTIES.SELECTED_SIZE]: selectedSize || '',
              });
              setShowSizeSheet(false);
              startCheckout();
            }}
          />
        )}

        {/* Full-Screen Zoomable Carousel Overlay */}
        {isFullScreen && (
          <ZoomableCarousel
            images={activeFullScreenImages}
            initialIndex={initialImageIndex}
            reviewOverlays={activeReviewOverlays}
            onClose={() => setIsFullScreen(false)}
          />
        )}

        {/* Full-Screen Reels Overlay Player */}
        {activeReelIndex !== null && (
          <ReelsPlayerOverlay
            videos={influencerVideos}
            initialIndex={activeReelIndex}
            onClose={() => setActiveReelIndex(null)}
          />
        )}
      </div>
    </div>
  );
};
export default ProductDetailPage;
